import { useState, useEffect, useRef } from 'react';
import { 
  Image as ImageIcon, Upload, Trash2, Loader2, Save, Eye,
  LayoutGrid, GalleryHorizontal
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomepageManager() {
  const [homepageContent, setHomepageContent] = useState({
    hero_image: null,
    about_images: [],
    gallery_preview_images: [],
    cta_background_image: null
  });
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState(null); // { type: 'hero' | 'about' | 'gallery_preview' | 'cta', index?: number }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, galleryRes] = await Promise.all([
        axios.get(`${API}/homepage-content`),
        axios.get(`${API}/gallery`)
      ]);
      setHomepageContent(contentRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/homepage-content`, homepageContent);
      toast.success('Homepage images saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openGallerySelector = (type, index = null) => {
    setCurrentSelection({ type, index });
    setGalleryDialogOpen(true);
  };

  const selectImage = (imageUrl) => {
    if (!currentSelection) return;
    
    const { type, index } = currentSelection;
    
    if (type === 'hero') {
      setHomepageContent(prev => ({ ...prev, hero_image: imageUrl }));
    } else if (type === 'cta') {
      setHomepageContent(prev => ({ ...prev, cta_background_image: imageUrl }));
    } else if (type === 'about') {
      setHomepageContent(prev => {
        const newImages = [...(prev.about_images || [])];
        if (index !== null && index < newImages.length) {
          newImages[index] = imageUrl;
        } else {
          newImages.push(imageUrl);
        }
        return { ...prev, about_images: newImages };
      });
    } else if (type === 'gallery_preview') {
      setHomepageContent(prev => {
        const newImages = [...(prev.gallery_preview_images || [])];
        if (index !== null && index < newImages.length) {
          newImages[index] = imageUrl;
        } else {
          newImages.push(imageUrl);
        }
        return { ...prev, gallery_preview_images: newImages };
      });
    }
    
    setGalleryDialogOpen(false);
  };

  const removeImage = (type, index = null) => {
    if (type === 'hero') {
      setHomepageContent(prev => ({ ...prev, hero_image: null }));
    } else if (type === 'cta') {
      setHomepageContent(prev => ({ ...prev, cta_background_image: null }));
    } else if (type === 'about') {
      setHomepageContent(prev => ({
        ...prev,
        about_images: prev.about_images.filter((_, i) => i !== index)
      }));
    } else if (type === 'gallery_preview') {
      setHomepageContent(prev => ({
        ...prev,
        gallery_preview_images: prev.gallery_preview_images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('category', 'homepage');

    try {
      const res = await axios.post(`${API}/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      selectImage(fullUrl);
      // Refresh gallery
      const galleryRes = await axios.get(`${API}/gallery`);
      setGallery(galleryRes.data);
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Homepage Images</h1>
          <p className="text-[#66605B]">Manage the images displayed on your homepage</p>
        </div>
        <div className="flex gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Preview
          </a>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-[#1B4D3E] hover:bg-[#153E32]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <ImageIcon className="w-5 h-5 text-[#1B4D3E]" />
          <h2 className="font-serif text-xl text-[#1C1C1C]">Hero Banner Image</h2>
        </div>
        <p className="text-sm text-[#66605B] mb-4">The main background image at the top of the homepage.</p>
        
        <div className="flex items-start gap-4">
          {homepageContent.hero_image ? (
            <div className="relative group">
              <img 
                src={homepageContent.hero_image} 
                alt="Hero" 
                className="w-64 h-40 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                <button
                  onClick={() => openGallerySelector('hero')}
                  className="p-2 bg-white rounded-lg hover:bg-gray-100"
                  title="Change image"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeImage('hero')}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  title="Remove image"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => openGallerySelector('hero')}
              className="w-64 h-40 border-2 border-dashed border-[#E5E0DB] rounded-lg flex flex-col items-center justify-center gap-2 text-[#66605B] hover:border-[#1B4D3E] hover:text-[#1B4D3E] transition-colors"
            >
              <Upload className="w-6 h-6" />
              <span className="text-sm">Select Image</span>
            </button>
          )}
        </div>
      </div>

      {/* About Section Images */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <LayoutGrid className="w-5 h-5 text-[#1B4D3E]" />
          <h2 className="font-serif text-xl text-[#1C1C1C]">About Section Images</h2>
        </div>
        <p className="text-sm text-[#66605B] mb-4">5 images displayed in the "Sanctuary on Jack Lake" section. First image is large, others are smaller.</p>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((idx) => {
            const imageUrl = homepageContent.about_images?.[idx];
            return (
              <div key={idx} className="relative">
                <Label className="text-xs text-[#66605B] mb-1 block">
                  {idx === 0 ? 'Main (Large)' : `Image ${idx + 1}`}
                </Label>
                {imageUrl ? (
                  <div className="relative group">
                    <img 
                      src={imageUrl} 
                      alt={`About ${idx + 1}`} 
                      className={`w-full object-cover rounded-lg ${idx === 0 ? 'h-32' : 'h-24'}`}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <button
                        onClick={() => openGallerySelector('about', idx)}
                        className="p-1.5 bg-white rounded-lg hover:bg-gray-100"
                      >
                        <ImageIcon className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeImage('about', idx)}
                        className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openGallerySelector('about', idx)}
                    className={`w-full border-2 border-dashed border-[#E5E0DB] rounded-lg flex flex-col items-center justify-center gap-1 text-[#66605B] hover:border-[#1B4D3E] hover:text-[#1B4D3E] transition-colors ${idx === 0 ? 'h-32' : 'h-24'}`}
                  >
                    <Upload className="w-4 h-4" />
                    <span className="text-xs">Select</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Gallery Preview Images */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <GalleryHorizontal className="w-5 h-5 text-[#1B4D3E]" />
          <h2 className="font-serif text-xl text-[#1C1C1C]">Gallery Preview Images</h2>
        </div>
        <p className="text-sm text-[#66605B] mb-4">6 images shown in the "Explore the Estate" gallery preview section. First image is featured (large).</p>
        
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          {[0, 1, 2, 3, 4, 5].map((idx) => {
            const imageUrl = homepageContent.gallery_preview_images?.[idx];
            return (
              <div key={idx} className="relative">
                <Label className="text-xs text-[#66605B] mb-1 block">
                  {idx === 0 ? 'Featured' : `Image ${idx + 1}`}
                </Label>
                {imageUrl ? (
                  <div className="relative group">
                    <img 
                      src={imageUrl} 
                      alt={`Gallery preview ${idx + 1}`} 
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <button
                        onClick={() => openGallerySelector('gallery_preview', idx)}
                        className="p-1.5 bg-white rounded-lg hover:bg-gray-100"
                      >
                        <ImageIcon className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeImage('gallery_preview', idx)}
                        className="p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => openGallerySelector('gallery_preview', idx)}
                    className="w-full h-24 border-2 border-dashed border-[#E5E0DB] rounded-lg flex flex-col items-center justify-center gap-1 text-[#66605B] hover:border-[#1B4D3E] hover:text-[#1B4D3E] transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="text-xs">Select</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA Background Image */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <ImageIcon className="w-5 h-5 text-[#1B4D3E]" />
          <h2 className="font-serif text-xl text-[#1C1C1C]">CTA Section Background</h2>
        </div>
        <p className="text-sm text-[#66605B] mb-4">Background image for the "Book Your Retreat" call-to-action section.</p>
        
        <div className="flex items-start gap-4">
          {homepageContent.cta_background_image ? (
            <div className="relative group">
              <img 
                src={homepageContent.cta_background_image} 
                alt="CTA Background" 
                className="w-64 h-40 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                <button
                  onClick={() => openGallerySelector('cta')}
                  className="p-2 bg-white rounded-lg hover:bg-gray-100"
                  title="Change image"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeImage('cta')}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  title="Remove image"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => openGallerySelector('cta')}
              className="w-64 h-40 border-2 border-dashed border-[#E5E0DB] rounded-lg flex flex-col items-center justify-center gap-2 text-[#66605B] hover:border-[#1B4D3E] hover:text-[#1B4D3E] transition-colors"
            >
              <Upload className="w-6 h-6" />
              <span className="text-sm">Select Image</span>
            </button>
          )}
        </div>
      </div>

      {/* Gallery Selection Dialog */}
      <Dialog open={galleryDialogOpen} onOpenChange={setGalleryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Image</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload new option */}
            <div className="flex items-center gap-4 p-4 bg-[#F9F8F6] rounded-lg">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload New Image
              </Button>
              <span className="text-sm text-[#66605B]">or select from gallery below</span>
            </div>

            {/* Gallery grid */}
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {gallery.map((img) => (
                <button
                  key={img.id}
                  onClick={() => selectImage(img.url)}
                  className="relative aspect-square rounded-lg overflow-hidden hover:ring-2 hover:ring-[#1B4D3E] transition-all"
                >
                  <img 
                    src={img.url} 
                    alt={img.alt} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
